/**
 * Set-piece library — localStorage-backed v1 storage.
 *
 * TODO(Phase 4 v2): replace this localStorage layer with the
 *   `tactics_set_pieces` Prisma table per
 *   `docs/scope/tactics-hub-roadmap.md` §Phase 4. The type shape
 *   defined in `@4cm/core/tactics/setPieces` is the source of truth
 *   for the SQL migration — the table columns mirror those fields
 *   one-for-one.
 *
 * Scope: named, recallable set-piece plays — attacking and defending
 * corners, free kicks (long / short / wide), throw-ins, penalties.
 * Mirrors `tacticsPatternsStore.ts` in its persistence pattern, but
 * differs in shape: set pieces carry a `type` (one of 8), an `origin`
 * (ball spot) and an optional `delivery` style on top of the position
 * snapshot.
 *
 * Public API:
 *   - listSetPieces()              — all saved set pieces, newest-first
 *   - getSetPiece(id)              — single lookup by id, or null
 *   - saveSetPiece(input)          — create (mints id + createdAt)
 *   - deleteSetPiece(id)           — hard-delete (v2 will soft-delete)
 *   - renameSetPiece(id, name)     — patch the `name` field only
 *   - filterByType(type)           — narrow the list to one tab
 */

import {
  isTacticsSetPiece,
  type SetPieceType,
  type TacticsSetPiece,
} from '@4cm/core/tactics/setPieces';

const STORE_KEY = '4cm_set_pieces';

type Store = Record<string, TacticsSetPiece>;

function readStore(): Store {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORE_KEY)
        : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Store;
    if (!parsed || typeof parsed !== 'object') return {};
    // Filter out corrupt entries so the renderer always sees clean data.
    const out: Store = {};
    for (const [id, entry] of Object.entries(parsed)) {
      if (isTacticsSetPiece(entry)) out[id] = entry;
    }
    return out;
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[setPiecesStore] could not persist', err);
  }
}

/** Stable id factory — Node 20+ and modern browsers expose crypto.randomUUID. */
function mintId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older runners — timestamp + random is fine for v1.
  return `sp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Input shape for `saveSetPiece` — auto-fields (`id`, `createdAt`)
 * are minted by the store, not provided by the caller.
 */
export type SaveSetPieceInput = Omit<TacticsSetPiece, 'id' | 'createdAt'>;

/** List all saved set pieces, sorted by `createdAt` desc (newest first). */
export function listSetPieces(): TacticsSetPiece[] {
  const store = readStore();
  const all = Object.values(store);
  all.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return all;
}

/** Look up a single set piece by id. Returns null when the id is
 *  unknown — used by the /setpieces → /2d|/3d deep-link, which carries
 *  a `setpiece=<id>` query param the board resolves on mount. */
export function getSetPiece(id: string): TacticsSetPiece | null {
  return readStore()[id] ?? null;
}

/** Insert a new set piece. Returns the persisted shape so the caller
 *  can read back the minted id + createdAt. */
export function saveSetPiece(input: SaveSetPieceInput): TacticsSetPiece {
  const store = readStore();
  const id = mintId();
  const entry: TacticsSetPiece = {
    ...input,
    id,
    createdAt: new Date().toISOString(),
  };
  store[id] = entry;
  writeStore(store);
  return entry;
}

/** Hard-delete a set piece. No-op when the id isn't present. */
export function deleteSetPiece(id: string): void {
  const store = readStore();
  if (!(id in store)) return;
  delete store[id];
  writeStore(store);
}

/** Rename a set piece in place. Returns the updated entry, or null
 *  when the id is unknown. */
export function renameSetPiece(
  id: string,
  name: string,
): TacticsSetPiece | null {
  const store = readStore();
  const prior = store[id];
  if (!prior) return null;
  const next: TacticsSetPiece = { ...prior, name };
  store[id] = next;
  writeStore(store);
  return next;
}

/** Filter the list to one set-piece type tab. */
export function filterByType(type: SetPieceType): TacticsSetPiece[] {
  return listSetPieces().filter((p) => p.type === type);
}
